import React, { useState, useCallback } from "react";
import TrebbleDropdown from "../common/TrebbleDropdown";

interface LibraryFilterPopupProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  activeFilter: string;
  onFilterChange: (filter: string) => void;
  activeSort: string;
  onSortChange: (sort: string) => void;
  typeOptions?: FilterOption[];
  sortOptions?: FilterOption[];
  ti18n?: Record<string, string>;
  children: React.ReactNode;
}

interface FilterOption {
  key: string;
  label: string;
}

const getI18nLabel = (ti18n: Record<string, string> | undefined, key: string, fallback: string): string => {
  if (ti18n && typeof window !== "undefined" && window.getI18n) {
    return window.getI18n(ti18n, key) || fallback;
  }
  return fallback;
};

const buildTypeFilterOptions = (ti18n?: Record<string, string>): FilterOption[] => [
  { key: "all", label: getI18nLabel(ti18n, "ALL", "All") },
  { key: "audio", label: getI18nLabel(ti18n, "AUDIO", "Audio") },
  { key: "video", label: getI18nLabel(ti18n, "VIDEO", "Video") },
];

const buildSortOptions = (ti18n?: Record<string, string>): FilterOption[] => [
  { key: "newest", label: getI18nLabel(ti18n, "NEWEST_FIRST", "Newest first") },
  { key: "oldest", label: getI18nLabel(ti18n, "OLDEST_FIRST", "Oldest first") },
  { key: "name_asc", label: getI18nLabel(ti18n, "NAME_A_Z", "Name A-Z") },
  { key: "name_desc", label: getI18nLabel(ti18n, "NAME_Z_A", "Name Z-A") },
];

const CONTAINER_STYLE: React.CSSProperties = {
  minWidth: 220,
  background: "var(--bg-default, #fff)",
  border: "none",
  boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
  padding: "8px 0",
};

const SECTION_LABEL_STYLE: React.CSSProperties = {
  padding: "8px 16px",
  fontSize: "var(--text-sm)",
  fontWeight: 700,
  color: "var(--text-muted)",
  textTransform: "uppercase",
  letterSpacing: "0.5px",
  lineHeight: 1.4,
};

const ROW_STYLE: React.CSSProperties = {
  padding: "8px 16px",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  cursor: "pointer",
  fontSize: "var(--text-base)",
  color: "var(--text-default-color)",
  transition: "background-color 0.15s ease",
  userSelect: "none",
};

const ACTIVE_ROW_ADDITIONS: React.CSSProperties = {
  fontWeight: 700,
};

const CHECK_ICON_STYLE: React.CSSProperties = {
  WebkitTextStroke: "0.5px",
  fontSize: "var(--text-lg)",
  color: "var(--text-default-color)",
  lineHeight: 1,
  flexShrink: 0,
};

const DIVIDER_STYLE: React.CSSProperties = {
  height: 1,
  background: "var(--border-default-color)",
  margin: "4px 0",
};

const LibraryFilterPopup: React.FC<LibraryFilterPopupProps> = ({
  open,
  onOpenChange,
  activeFilter,
  onFilterChange,
  activeSort,
  onSortChange,
  typeOptions,
  sortOptions,
  ti18n,
  children,
}) => {
  const [hoveredKey, setHoveredKey] = useState<string | null>(null);
  const resolvedTypeOptions = typeOptions === undefined ? buildTypeFilterOptions(ti18n) : typeOptions;
  const resolvedSortOptions = sortOptions === undefined ? buildSortOptions(ti18n) : sortOptions;
  const typeLabel = getI18nLabel(ti18n, "TYPE", "Type");
  const sortByLabel = getI18nLabel(ti18n, "SORT_BY", "Sort by");

  const handleFilterSelect = useCallback(
    (key: string) => {
      onFilterChange(key);
      onOpenChange(false);
    },
    [onFilterChange, onOpenChange]
  );

  const handleSortSelect = useCallback(
    (key: string) => {
      onSortChange(key);
      onOpenChange(false);
    },
    [onSortChange, onOpenChange]
  );

  const renderRow = (
    option: FilterOption,
    isActive: boolean,
    onSelect: (key: string) => void
  ) => {
    const isHovered = hoveredKey === option.key;

    const rowStyle: React.CSSProperties = {
      ...ROW_STYLE,
      ...(isActive ? ACTIVE_ROW_ADDITIONS : {}),
      ...(isHovered ? { background: "var(--color-black-10)" } : {}),
    };

    return (
      <div
        key={option.key}
        style={rowStyle}
        onClick={() => onSelect(option.key)}
        onMouseEnter={() => setHoveredKey(option.key)}
        onMouseLeave={() => setHoveredKey(null)}
        role="menuitem"
        aria-current={isActive ? "true" : undefined}
      >
        <span>{option.label}</span>
        {isActive && (
          <i className="pe-7s-check" style={CHECK_ICON_STYLE} />
        )}
      </div>
    );
  };

  /* Dark mode note: body.darkTheme overrides --color-white-90, --color-black-10,
     --shadow-overlay, etc. via CSS custom properties, so frosted-glass appearance
     adapts automatically. The overlayClassName can be targeted in styles.css for
     any additional dark-mode adjustments if needed. */
  const dropdownContent = (
    <div style={CONTAINER_STYLE} role="menu">
      {resolvedTypeOptions.length > 0 && (
        <>
          <div style={SECTION_LABEL_STYLE}>{typeLabel}</div>
          {resolvedTypeOptions.map((option) =>
            renderRow(option, activeFilter === option.key, handleFilterSelect)
          )}
        </>
      )}

      {resolvedTypeOptions.length > 0 && resolvedSortOptions.length > 0 && (
        <div style={DIVIDER_STYLE} />
      )}

      {resolvedSortOptions.length > 0 && (
        <>
          <div style={SECTION_LABEL_STYLE}>{sortByLabel}</div>
          {resolvedSortOptions.map((option) =>
            renderRow(option, activeSort === option.key, handleSortSelect)
          )}
        </>
      )}
    </div>
  );

  return (
    <TrebbleDropdown
      useEditorTheme={false}
      dropdownRender={() => dropdownContent}
      trigger={["click"]}
      open={open}
      onOpenChange={onOpenChange}
      placement="bottomRight"
      overlayClassName="library-filter-dropdown"
    >
      {children}
    </TrebbleDropdown>
  );
};

export default LibraryFilterPopup;
